/* Home hero: decode-first mobile crossfade; desktop collage stays native. */
if (!customElements.get('sc-hero-collage')) {
  customElements.define('sc-hero-collage', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sc-hero__track');
      this.slides = [...this.querySelectorAll('.sc-hero__slide')];
      this.thumbs = [...this.querySelectorAll('.sc-hero__thumb')];
      this.status = this.querySelector('.sc-hero__status');
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const listen = (target, name, fn, options = {}) => target.addEventListener(name, fn, { ...options, signal: this.abort.signal });
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.request = 0;
      this.images = new Map();
      this.paused = this.reduced.matches || !!window.Shopify?.designMode;
      this.thumbs.forEach((button, index) => {
        listen(button, 'click', () => { this.stop(); void this.go(index); });
        listen(button, 'keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.slides.length;
          if (event.key === 'ArrowLeft') next = (index + this.slides.length - 1) % this.slides.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.slides.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.stop();
          this.thumbs[next].focus({ preventScroll: true });
          void this.go(next);
        });
      });
      // No Pause button: hover, focus or touch stops autoplay for this visit.
      listen(this, 'pointerdown', (event) => { this.stop(); this.pointerX = event.clientX; }, { passive: true });
      listen(this, 'pointerup', (event) => {
        const distance = event.clientX - this.pointerX;
        this.pointerX = undefined;
        if (this.mobile.matches && Math.abs(distance) > 40) void this.go((this.index + (distance < 0 ? 1 : this.slides.length - 1)) % this.slides.length);
      });
      listen(this, 'pointercancel', () => { this.pointerX = undefined; });
      listen(this, 'focusin', () => this.stop());
      listen(this, 'pointerenter', (event) => { if (event.pointerType === 'mouse') this.stop(); });
      listen(document, 'visibilitychange', () => this.autoplay());
      listen(this.mobile, 'change', () => { this.select(this.index); this.autoplay(); });
      listen(this.reduced, 'change', () => { if (this.reduced.matches) this.stop(); });
      listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('.sc-hero__slide'));
        if (index >= 0) { this.stop(); void this.go(index); }
      });
      this.select(0);
      this.setAttribute('data-ready', '');
      const first = this.slides[0].querySelector('img');
      const afterPaint = () => {
        if (this.afterFirstPaint || !this.isConnected) return;
        this.afterFirstPaint = true;
        this.lcpObserver?.disconnect();
        clearTimeout(this.paintFallback);
        this.slides.forEach((_, index) => { if (index) void this.readyImage(index).catch(() => {}); });
        this.autoplay();
      };
      // LCP entries can cease after early input. Still hydrate after the first image has painted.
      const paintedFallback = () => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.afterFirstPaint) this.paintFallback = setTimeout(afterPaint, 150);
      }));
      if (!first || first.complete) paintedFallback();
      else listen(first, 'load', paintedFallback, { once: true });
      if (first) listen(first, 'error', afterPaint, { once: true });
      if (first && window.PerformanceObserver?.supportedEntryTypes?.includes('largest-contentful-paint')) {
        this.lcpObserver = new PerformanceObserver(list => {
          if (list.getEntries().some(entry => entry.element === first)) afterPaint();
        });
        this.lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.lcpObserver?.disconnect();
      clearInterval(this.timer);
      clearTimeout(this.fadeTimer);
      clearTimeout(this.paintFallback);
      this.fadeResolve?.();
      this.request++;
    }
    stop() { this.paused = true; this.request++; this.autoplay(); }
    readyImage(index) {
      if (this.images.has(index)) return this.images.get(index);
      const slide = this.slides[index], img = slide.querySelector('img');
      const ready = (async () => {
        if (!img) return;
        slide.querySelectorAll('source[data-sc-hero-srcset]').forEach(source => {
          source.srcset = source.dataset.scHeroSrcset;
          source.removeAttribute('data-sc-hero-srcset');
        });
        if (img.dataset.scHeroSrc) {
          img.loading = 'eager'; // Only invoked after first hero paint or explicit interaction.
          if (img.dataset.scHeroSrcset) img.srcset = img.dataset.scHeroSrcset;
          img.src = img.dataset.scHeroSrc;
          img.removeAttribute('data-sc-hero-src');
          img.removeAttribute('data-sc-hero-srcset');
        }
        await img.decode();
        img.classList.remove('sc-image-skeleton');
      })();
      const checked = ready.catch(error => { this.images.delete(index); throw error; });
      this.images.set(index, checked);
      return checked;
    }
    select(index) {
      this.index = index;
      this.thumbs.forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('data-active', i === index);
        const inactive = this.mobile.matches && i !== index;
        slide.inert = inactive;
        if (inactive) slide.setAttribute('aria-hidden', 'true');
        else slide.removeAttribute('aria-hidden');
      });
      if (this.status && this.paused) this.status.textContent = `Banner ${index + 1} of ${this.slides.length}`;
    }
    async go(index) {
      const request = ++this.request;
      try { await this.readyImage(index); } catch {
        if (this.status && this.paused && request === this.request) this.status.textContent = 'Banner could not load. Please try another banner.';
        return; // Keep the current image; a later manual action can recheck decoding.
      }
      await this.fading;
      if (!this.isConnected || request !== this.request || index === this.index) return;
      this.slides.forEach(slide => slide.removeAttribute('data-outgoing'));
      const old = this.slides[this.index];
      old.setAttribute('data-outgoing', '');
      this.select(index);
      // Serialize fades: rapid retargeting never discards the only opaque backing.
      this.fading = new Promise(resolve => {
        this.fadeResolve = resolve;
        this.fadeTimer = setTimeout(() => { old.removeAttribute('data-outgoing'); resolve(); }, this.reduced.matches ? 0 : 260);
      });
    }
    autoplay() {
      clearInterval(this.timer);
      this.timer = null;
      if (!this.afterFirstPaint || this.dataset.autoplay !== 'true' || this.slides.length < 2 || !this.mobile.matches || this.paused || this.reduced.matches || document.hidden) return;
      this.timer = setInterval(() => { void this.go((this.index + 1) % this.slides.length); }, Number(this.dataset.interval) || 6000);
    }
  });
}
